import type { HelpContent } from './types';

export const projectStatusHelp: HelpContent = {
  pageTitle: 'Project Status Report',
  description: 'A portfolio-wide view of project health, delivery status, and key indicators across all your projects.',

  functionality: {
    overview: 'The Project Status Report gives you an at-a-glance view of the health and delivery status of every project in your portfolio. Use it to quickly identify projects that are at risk, off track, or need attention.',

    keyFeatures: [
      {
        label: 'Portfolio health summary',
        detail: 'KPI cards at the top show the count of projects by health status — On Track, At Risk, and Critical — giving an immediate portfolio-wide view.',
      },
      {
        label: 'Health indicators',
        detail: 'Each project displays a RAG (Red, Amber, Green) health indicator so you can instantly identify which projects need attention.',
      },
      {
        label: 'Status and delivery information',
        detail: 'See project status, start and end dates, project manager, and delivery health for every project in one consolidated view.',
      },
      {
        label: 'Search and filter',
        detail: 'Search by project name or client, and filter by status or health to focus on specific subsets of your portfolio.',
      },
      {
        label: 'Drill into project details',
        detail: 'Click any project row to navigate directly to the full Project Details page for that project.',
      },
      {
        label: 'Export to Excel',
        detail: 'Export the project status report to Excel, reflecting any active filters.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'KPI summary cards',
        description: 'Shows the count of projects in each health state: On Track, At Risk, and Critical. Clicking a card filters the table to that health status.',
      },
      {
        name: 'Search bar',
        description: 'Filter the project list in real time by project name or client name.',
      },
      {
        name: 'Status filter',
        description: 'Filter projects by their current status — Active, On Hold, Completed, or Cancelled.',
      },
      {
        name: 'Health filter',
        description: 'Filter projects by their health indicator — On Track, At Risk, or Critical.',
      },
      {
        name: 'Project status table',
        description: 'Lists all projects with columns for project name, client, status, health indicator, project manager, start date, end date, and budget health.',
      },
      {
        name: 'Health indicator',
        description: 'A colour-coded RAG indicator showing the overall health of the project — green (On Track), amber (At Risk), or red (Critical).',
      },
      {
        name: 'Export button',
        description: 'Downloads the current project status report as an Excel file.',
      },
    ],

    useCases: [
      'Getting a portfolio-wide view of project health at a glance.',
      'Identifying projects that are at risk or critical before a steering committee meeting.',
      'Filtering to a specific project manager\'s projects to review their portfolio.',
      'Exporting status data for use in board reports or stakeholder updates.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Project Status Report loads with all active projects. Use the KPI cards for an immediate health summary, then use the filters to drill into specific areas of concern.',
    steps: [
      {
        title: 'How do I find projects that are at risk?',
        items: [
          { text: 'Click the "At Risk" or "Critical" KPI card to filter the table to those projects.' },
          { text: 'Alternatively, use the health filter dropdown to select the status you want to view.' },
        ],
      },
      {
        title: 'How do I view full details for a project?',
        items: [
          { text: 'Click any project row in the table to navigate to the Project Details page for that project.' },
        ],
      },
      {
        title: 'How do I export the report?',
        items: [
          { text: 'Apply any filters you need to refine the data.' },
          { text: 'Click the Export button in the page header.' },
          { text: 'The report downloads as an Excel file.' },
        ],
      },
    ],
    proTips: [
      'Click directly on a KPI card to instantly filter the table — it\'s faster than using the filter dropdown.',
      'Use the status filter set to "Active" combined with the "Critical" health filter to focus only on live projects that need urgent attention.',
    ],
  },

  faq: [
    {
      question: 'How is the health indicator calculated?',
      answer: 'The health indicator is set manually on each project by the project manager, or can be triggered automatically by threshold rules configured in Settings. A green indicator means On Track, amber means At Risk, and red means Critical.',
    },
    {
      question: 'Can I update a project\'s health status from this report?',
      answer: 'No. The Project Status Report is read-only. To update a project\'s health status, navigate to the Project Details page and update it from there.',
    },
    {
      question: 'Why is a project not showing in the report?',
      answer: 'Projects that have been archived or deleted will not appear. If you cannot find a project, check the status filter — it may be filtered to Active projects only.',
    },
    {
      question: 'Can I see all projects or only the ones I manage?',
      answer: 'The projects visible to you depend on your access settings. If you believe you are missing visibility of certain projects, contact your Administrator.',
    },
  ],

  relatedPages: [
    { label: 'Projects', path: '/projects' },
    { label: 'Budget Variance Report', path: '/reports/budget-variance' },
    { label: 'Profitability Report', path: '/reports/profitability' },
  ],
};
